import React, { useState, useEffect } from "react";
import createInstance from "../../../api/axiosinstance";
import Toast from "../../../utils/toast";
import Loader from "../../Loader";

// MUI Components
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import SendIcon from "@mui/icons-material/Send";

const FeedbackForm = ({ isOpen, onClose, organizationId, interviewId }) => {
  // Form state
  const [formData, setFormData] = useState({
    candidateName: "",
    position: "",
    interviewDate: "",
    interviewerName: "",
    roundType: "",
    technicalKnowledge: {
      notes: "",
      score: "",
      comments: "",
    },
    handsOnSkills: {
      notes: "",
      score: "",
      comments: "",
    },
    analyticalThinking: {
      notes: "",
      score: "",
      comments: "",
    },
    communicationSkills: {
      notes: "",
      score: "",
      comments: "",
    },
    culturalFit: {
      notes: "",
      score: "",
      comments: "",
    },
    additionalObservations: "",
    decision: "",
    nextRoundRecommendation: "",
    remarksForHR: "",
  });

  // Add loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const aiDishaApi = createInstance("DISHA");

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [section, field] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Calculate average rating
  const calculateAverageRating = () => {
    const scores = [
      parseInt(formData.technicalKnowledge.score) || 0,
      parseInt(formData.handsOnSkills.score) || 0,
      parseInt(formData.analyticalThinking.score) || 0,
      parseInt(formData.communicationSkills.score) || 0,
      parseInt(formData.culturalFit.score) || 0,
    ];

    const validScores = scores.filter((score) => score > 0);
    if (validScores.length === 0) return 0;

    const sum = validScores.reduce((a, b) => a + b, 0);
    return Math.round(sum / validScores.length);
  };

  // Form submission
  // http://194.195.114.60:8000/update_interview_status_and_feedback?interview_id=INTID000167&organization_id=ORGID000002&decision=Strong%20Hire&next_round_recommendation=ok&remarks=ok
  const handleFormSubmit = async (event) => {
    event.preventDefault();

    const rating = calculateAverageRating();

    // Validate form
    if (rating === 0) {
      Toast.error("Please provide ratings for at least one category");
      return;
    }

    if (!formData.decision) {
      Toast.error("Please select a decision");
      return;
    }

    // Prepare request body according to the API spec
    const requestBody = {
      Detail_schema: {
        Technical_knowledge_detail: formData.technicalKnowledge.notes || "N/A",
        Hands_on_skills_detail: formData.handsOnSkills.notes || "N/A",
        Problem_solving_detail: formData.analyticalThinking.notes || "N/A",
        Communication_skills_detail:
          formData.communicationSkills.notes || "N/A",
        Culture_fit_detail: formData.culturalFit.notes || "N/A",
      },
      scorecard: {
        Technical_knowledge: [
          {
            rating: parseInt(formData.technicalKnowledge.score) || 0,
            comment: formData.technicalKnowledge.comments || "",
          },
        ],
        Hands_on_skills: [
          {
            rating: parseInt(formData.handsOnSkills.score) || 0,
            comment: formData.handsOnSkills.comments || "",
          },
        ],
        Problem_solving: [
          {
            rating: parseInt(formData.analyticalThinking.score) || 0,
            comment: formData.analyticalThinking.comments || "",
          },
        ],
        Communication_skills: [
          {
            rating: parseInt(formData.communicationSkills.score) || 0,
            comment: formData.communicationSkills.comments || "",
          },
        ],
        Culture_fit: [
          {
            rating: parseInt(formData.culturalFit.score) || 0,
            comment: formData.culturalFit.comments || "",
          },
        ],
      },
    };

    try {
      // Set loading state to true
      setIsSubmitting(true);

      // Modified API call to include the request body
      const response = await aiDishaApi.put(
        `/update_interview_status_and_feedback?interview_id=${interviewId}&organization_id=${organizationId}&decision=${encodeURIComponent(
          formData.decision
        )}&next_round_recommendation=${encodeURIComponent(
          formData.nextRoundRecommendation
        )}&remarks=${encodeURIComponent(formData.remarksForHR)}`,
        requestBody
      );

      Toast.success("Feedback form submitted successfully");
      clearForm();
      onClose();
    } catch (error) {
      console.error("Error saving feedback:", error);
      Toast.error("Failed to submit feedback");
    } finally {
      // Set loading state back to false
      setIsSubmitting(false);
    }
  };
  // Clear form fields
  const clearForm = () => {
    setFormData({
      candidateName: "",
      position: "",
      interviewDate: "",
      interviewerName: "",
      roundType: "",
      technicalKnowledge: {
        notes: "",
        score: "",
        comments: "",
      },
      handsOnSkills: {
        notes: "",
        score: "",
        comments: "",
      },
      analyticalThinking: {
        notes: "",
        score: "",
        comments: "",
      },
      communicationSkills: {
        notes: "",
        score: "",
        comments: "",
      },
      culturalFit: {
        notes: "",
        score: "",
        comments: "",
      },
      additionalObservations: "",
      decision: "",
      nextRoundRecommendation: "",
      remarksForHR: "",
    });
  };

  // Modal style
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    maxWidth: 1000,
    maxHeight: "90vh",
    overflow: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 0,
    borderRadius: 1,
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="interview-feedback-form"
      aria-describedby="comprehensive-it-interview-feedback-form"
    >
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            bgcolor: "#006A71",
            color: "white",
          }}
        >
          <Typography variant="h6" component="h2">
            IT Interview Feedback Form
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleFormSubmit} sx={{ p: 3 }}>
          {/* Candidate & Interview Details */}
          {/* <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Candidate Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id="candidateName"
                    name="candidateName"
                    label="Candidate Name"
                    value={formData.candidateName}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id="position"
                    name="position"
                    label="Position Applied For"
                    value={formData.position}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id="interviewDate"
                    name="interviewDate"
                    label="Interview Date"
                    type="date"
                    value={formData.interviewDate}
                    onChange={handleInputChange}
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id="interviewerName"
                    name="interviewerName"
                    label="Interviewer Name"
                    value={formData.interviewerName}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="roundType-label">Interview Round</InputLabel>
                    <Select
                      labelId="roundType-label"
                      id="roundType"
                      name="roundType"
                      value={formData.roundType}
                      label="Interview Round"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="">Choose...</MenuItem>
                      <MenuItem value="Technical Round 1">Technical Round 1</MenuItem>
                      <MenuItem value="Technical Round 2">Technical Round 2</MenuItem>
                      <MenuItem value="Managerial">Managerial</MenuItem>
                      <MenuItem value="HR">HR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card> */}

          {/* Feedback Sections */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detailed Feedback
              </Typography>

              <TextField
                fullWidth
                id="technicalKnowledge-notes"
                name="technicalKnowledge.notes"
                label="Technical Knowledge"
                multiline
                rows={3}
                value={formData.technicalKnowledge.notes}
                onChange={handleInputChange}
                margin="normal"
              />

              <TextField
                fullWidth
                id="handsOnSkills-notes"
                name="handsOnSkills.notes"
                label="Hands-on Skills"
                multiline
                rows={3}
                value={formData.handsOnSkills.notes}
                onChange={handleInputChange}
                margin="normal"
              />

              <TextField
                fullWidth
                id="analyticalThinking-notes"
                name="analyticalThinking.notes"
                label="Analytical & Logical Thinking"
                multiline
                rows={3}
                value={formData.analyticalThinking.notes}
                onChange={handleInputChange}
                margin="normal"
              />

              <TextField
                fullWidth
                id="communicationSkills-notes"
                name="communicationSkills.notes"
                label="Communication & Soft Skills"
                multiline
                rows={3}
                value={formData.communicationSkills.notes}
                onChange={handleInputChange}
                margin="normal"
              />

              <TextField
                fullWidth
                id="culturalFit-notes"
                name="culturalFit.notes"
                label="Behavioral / Cultural Fit"
                multiline
                rows={3}
                value={formData.culturalFit.notes}
                onChange={handleInputChange}
                margin="normal"
              />

              <TextField
                fullWidth
                id="additionalObservations"
                name="additionalObservations"
                label="Additional Observations"
                multiline
                rows={3}
                value={formData.additionalObservations}
                onChange={handleInputChange}
                margin="normal"
              />
            </CardContent>
          </Card>

          {/* Scorecard Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Scorecard (1 - Poor, 5 - Excellent)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.100" }}>
                      <TableCell>Category</TableCell>
                      <TableCell align="center">Score (1-5)</TableCell>
                      <TableCell>Comments</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Technical Knowledge</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          inputProps={{ min: 1, max: 5 }}
                          name="technicalKnowledge.score"
                          value={formData.technicalKnowledge.score}
                          onChange={handleInputChange}
                          size="small"
                          sx={{ width: "80px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          name="technicalKnowledge.comments"
                          value={formData.technicalKnowledge.comments}
                          onChange={handleInputChange}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Hands-on Skills</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          inputProps={{ min: 1, max: 5 }}
                          name="handsOnSkills.score"
                          value={formData.handsOnSkills.score}
                          onChange={handleInputChange}
                          size="small"
                          sx={{ width: "80px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          name="handsOnSkills.comments"
                          value={formData.handsOnSkills.comments}
                          onChange={handleInputChange}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Problem Solving & Logic</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          inputProps={{ min: 1, max: 5 }}
                          name="analyticalThinking.score"
                          value={formData.analyticalThinking.score}
                          onChange={handleInputChange}
                          size="small"
                          sx={{ width: "80px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          name="analyticalThinking.comments"
                          value={formData.analyticalThinking.comments}
                          onChange={handleInputChange}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Communication Skills</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          inputProps={{ min: 1, max: 5 }}
                          name="communicationSkills.score"
                          value={formData.communicationSkills.score}
                          onChange={handleInputChange}
                          size="small"
                          sx={{ width: "80px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          name="communicationSkills.comments"
                          value={formData.communicationSkills.comments}
                          onChange={handleInputChange}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Attitude & Culture Fit</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          inputProps={{ min: 1, max: 5 }}
                          name="culturalFit.score"
                          value={formData.culturalFit.score}
                          onChange={handleInputChange}
                          size="small"
                          sx={{ width: "80px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          name="culturalFit.comments"
                          value={formData.culturalFit.comments}
                          onChange={handleInputChange}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Final Decision */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Final Recommendation
              </Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel id="decision-label">Decision</InputLabel>
                <Select
                  labelId="decision-label"
                  id="decision"
                  name="decision"
                  value={formData.decision}
                  label="Decision"
                  onChange={handleInputChange}
                  sx = {{ color : "black" }}
                >
                  <MenuItem value="" sx = {{ color : "black" }}>Select Decision</MenuItem>
                  <MenuItem value="Strong Hire" sx = {{ color : "black" }}>Strong Hire</MenuItem>
                  <MenuItem value="Hire" sx = {{ color : "black" }}>Hire</MenuItem>
                  <MenuItem value="Borderline" sx = {{ color : "black" }}>Borderline</MenuItem>
                  <MenuItem value="No Hire" sx = {{ color : "black" }}>No Hire</MenuItem>
                  <MenuItem value="Keep in Pipeline" sx = {{ color : "black" }}>Keep in Pipeline</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                id="nextRoundRecommendation"
                name="nextRoundRecommendation"
                label="Next Round Recommendation"
                value={formData.nextRoundRecommendation}
                onChange={handleInputChange}
                margin="normal"
              />

              <TextField
                fullWidth
                id="remarksForHR"
                name="remarksForHR"
                label="Remarks for HR/Next Interviewer"
                multiline
                rows={3}
                value={formData.remarksForHR}
                onChange={handleInputChange}
                margin="normal"
              />
            </CardContent>
          </Card>
          {isSubmitting && <Loader />}

          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}
          >
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ClearIcon />}
              onClick={clearForm}
              disabled={isSubmitting}
            >
              Clear Form
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              disabled={isSubmitting}
            >
              Submit Feedback
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default FeedbackForm;
